import { useSelector } from "react-redux";
import { useMemo, useState, useEffect } from "react";
import { RootState } from "app/store";
import { userIconPrefixes } from "app/assets";
import Constants from "common/constant";
import I18n from "features/shared/i18n";
import { CheckMark } from "features/user/assets";
import { EditorState } from "../types";

type IconCategory = "ALL" | "BOY" | "GIRL";

const TAB_OPTIONS = [
  { key: "ALL" as IconCategory, labelKey: "MSG_ICON_CATEGORY_ALL" },
  { key: "BOY" as IconCategory, labelKey: "MSG_ICON_CATEGORY_BOY_BY_PARENT" },
  { key: "GIRL" as IconCategory, labelKey: "MSG_ICON_CATEGORY_GIRL_BY_PARENT" },
];

interface IconData {
  url: string;
  category: string;
  key: string;
}

interface PresetIconStepProps {
  state: EditorState;
  updateState: (updates: Partial<EditorState>) => void;
}

export const PresetIconStep = ({ state, updateState }: PresetIconStepProps) => {
  const [selectedTab, setSelectedTab] = useState<IconCategory>("ALL");
  const defaultAssets = useSelector((s: RootState) => s.config.assets);

  const icons = useMemo(() => {
    try {
      type AssetItem = { fileSize: number; fileKey: string; category: string };
      return userIconPrefixes
        .reduce((pre: AssetItem[], cur: string) => [...pre, ...defaultAssets[cur]], [])
        .filter((a: AssetItem) => a.fileSize > 0)
        .map((a: AssetItem) => ({
          url: `${Constants.assetHost}/${a.fileKey}`,
          category: a.category,
          key: a.fileKey,
        }));
    } catch {
      return [] as IconData[];
    }
  }, [defaultAssets]);

  useEffect(() => {
    if (!state.icon && icons.length > 0) {
      updateState({ icon: icons[0].url });
    }
  }, [icons, state.icon, updateState]);

  const handleTabChange = (tab: IconCategory) => {
    setSelectedTab(tab);
  };

  const handleSelectIcon = (icon: IconData) => {
    updateState({ icon: icon.url });
  };

  const filteredIcons =
    selectedTab === "ALL" ? icons : icons.filter((i) => i.category === selectedTab);

  return (
    <div className="flex-col-el h-full min-h-0">
      {/* カテゴリタブ */}
      <div className="flex-row-center h-[74px] mb-4 bg-beige-60 rounded-[12px] border-[4px] border-solid border-white">
        {TAB_OPTIONS.map((option, index) => {
          const isActive = selectedTab === option.key;
          const isFirst = index === 0;
          const isLast = index === TAB_OPTIONS.length - 1;
          return (
            <div
              key={option.key}
              onClick={() => handleTabChange(option.key)}
              className={`flex-col-center cursor-pointer flex-1 h-full ${
                isActive ? "bg-green-alt" : ""
              } ${isFirst ? "rounded-l-[8px]" : ""} ${isLast ? "rounded-r-[8px]" : ""}`}
            >
              <p className="flex-col-center text text-textcolor-black !text-[20px] !leading-[22px]">
                {I18n.t(option.labelKey)}
              </p>
            </div>
          );
        })}
      </div>

      {/* アイコングリッド */}
      <div className="flex-col-el flex-1 overflow-y-auto p-4">
        <div className="grid grid-cols-7 gap-4">
          {filteredIcons.map((icon) => (
            <div
              key={icon.key}
              onClick={() => handleSelectIcon(icon)}
              className="flex-col-center w-[90px] h-[90px] cursor-pointer relative"
            >
              {state.icon === icon.url && (
                <div className="flex-col-el absolute top-[-16px] left-[-16px] z-50">
                  <CheckMark />
                </div>
              )}
              <img
                src={icon.url}
                alt=""
                className={`flex-col-center w-full h-full object-contain border-solid border-6 ${
                  state.icon === icon.url ? "border-green-100/40" : "border-gray-80/40"
                }`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
